'use client';
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { ShieldAlert, LogOut, Info, Server, RefreshCw } from 'lucide-react';
import axios from 'axios';

import { useLanguage } from '@/components/LanguageProvider';

interface UserType {
  id?: number;
  accessSuffix?: string | null;
  nickname: string;
  oauthId: string;
  email?: string;
  bio?: string;
  crystals?: number;
  playerId?: string;
  hasAccess?: boolean;
  role?: string;
  roles?: Array<{ id: number; role: string }>;
  isBanned?: boolean;
  bannedUntil?: string | null;
  banReason?: string | null;
  warningRestrictionUntil?: string | null;
  mcNickname?: string;
  mcBedrock?: string;
}

interface RegionData {
  countryCode: string;
  currency: string;
  isAdmin: boolean;
  rules: any[];
}

type AuthStatus = 'LOADING' | 'GUEST' | 'NEEDS_REGISTRATION' | 'REGISTERED';

interface AuthContextType {
  status: AuthStatus;
  user: UserType | null;
  regionData: RegionData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  openLoginModal: () => void;
  closeLoginModal: () => void;
  logout: () => void;
  register: (selectedNickname?: string) => Promise<void>;
  refreshUser: () => Promise<boolean>;
}

// ДОБАВЛЯЕМ ИНТЕРФЕЙС ДЛЯ ПРОПСОВ ПРОВАЙДЕРА
interface AuthProviderProps {
  children: React.ReactNode;
  initialUser?: UserType | null;
  initialStatus?: AuthStatus;
  initialRegionData?: RegionData | null;
}

const AuthContext = createContext<AuthContextType>({} as any);

const IDP_API_URL = 'https://sg-public-api.royolands.com/api';
const LOCAL_API_URL = process.env.NEXT_PUBLIC_LOCAL_API || 'https://sg-public-api.royolands.com/rpweb';
const CLIENT_ID = 'russia';
const REDIRECT_URI = 'https://rplands.royolands.com';

const SILENT_CHECK_INTERVAL_MS = 3 * 60 * 1000; 

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void, reject: (reason?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });
    failedQueue = [];
};

export const api = axios.create({
    withCredentials: true,
});

const refreshAuthToken = async () => {
    try {
        const res = await fetch(`${LOCAL_API_URL}/auth/refresh`, {
            method: 'POST',
            credentials: 'include'
        });
        
        if (res.ok) {
            return true; 
        }
    } catch (e) {
        console.warn("Local refresh failed, falling back to SSO", e);
    }

    const ssoUrl = `${IDP_API_URL}/auth/check-cross-domain?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}`;
    
    const fetchSSO = async () => {
        const res = await fetch(ssoUrl, { credentials: 'include', cache: 'no-store' });
        if (!res.ok) throw new Error('Bad SSO response');
        return await res.json();
    };

    let data = await fetchSSO();
    if (!data.authenticated) data = await fetchSSO(); // fallback retry

    if (data.authenticated && data.auth_code && data.code_verifier) {
        const loginRes = await fetch(`${LOCAL_API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: data.auth_code, code_verifier: data.code_verifier }),
            credentials: 'include'
        });
        if (loginRes.ok) return true;
    }
    
    throw new Error("SSO Auth failed completely");
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const ignoredUrls = ['/auth/me', '/api/auth/check-cross-domain', 'check-cross-domain', '/auth/login'];
        const isIgnoredUrl = ignoredUrls.some(url => originalRequest.url?.includes(url));

        if (error.response?.status === 401 && !originalRequest._retry && !isIgnoredUrl) {
            originalRequest._retry = true; 
            if (originalRequest.data && typeof originalRequest.data === 'string') {
                try { originalRequest.data = JSON.parse(originalRequest.data); } catch (e) {}
            }
            if (isRefreshing) {
                return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }))
                       .then(() => api(originalRequest))
                       .catch((err) => Promise.reject(err));
            }
            isRefreshing = true;
            try {
                await refreshAuthToken();
                isRefreshing = false;
                processQueue(null, 'success');
                return api(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                processQueue(refreshError, null);
                return Promise.reject(refreshError);
            }
        }
        return Promise.reject(error);
    }
);

function generateRandomString(length: number) {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  return Array.from({ length }, () => possible.charAt(Math.floor(Math.random() * possible.length))).join('');
}

async function generateCodeChallenge(codeVerifier: string) {
  const encoder = new TextEncoder();
  const digest = await window.crypto.subtle.digest('SHA-256', encoder.encode(codeVerifier));
  return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

const LOAD_STYLES = `
.loading-cube-standalone { width: 72px; height: 72px; background: #121214; border: 1px solid rgba(255,255,255,0.05); border-radius: 20px; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5); animation: fadeScaleIn 0.3s ease-out; }
@keyframes fadeScaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
.spinner { width: 28px; height: 28px; border: 3px solid rgba(255, 255, 255, 0.1); border-left-color: #3b82f6; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { 100% { transform: rotate(360deg); } }
`;

const RegistrationModal = ({ user, regionData, onConfirm, onCancel, isProcessing, refreshUser }: any) => {
  const { t } = useLanguage();
  const tr = t.auth.reg;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const hasJava = !!user.mcNickname;
  const hasBedrock = !!user.mcBedrock;
  const hasAnyAccount = hasJava || hasBedrock;

  const [agreed, setAgreed] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<'java' | 'bedrock'>(hasJava ? 'java' : 'bedrock');
  const [isRefreshingState, setIsRefreshingState] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshingState(true);
    await refreshUser();
    setIsRefreshingState(false);
  };

  const cisCountries = [
    'RU', 'KZ', 'GE', 'AZ', 'KG', 'TM', 'TJ', 'MN', 'UZ', 'BY', 'MD', 'IL', 'UA'
  ];

  const serverIp = (regionData?.countryCode && cisCountries.includes(regionData.countryCode.toUpperCase()))
    ? 'rp.royolands.com'
    : 'play.royolands.com';

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4">
      <div className="bg-[#18181b] border border-white/10 rounded-[24px] w-full max-w-md p-6 shadow-xl relative">
        {!hasAnyAccount ? (
          <>
            <h2 className="text-xl font-bold text-white mb-2">{tr.bindTitle}</h2>
            <p className="text-zinc-400 text-sm mb-4">{tr.bindDesc}</p>

            <div className="bg-indigo-500/10 border border-indigo-500/20 p-4 rounded-xl mb-6">
              <div className="flex items-center gap-3 mb-3">
                <Server className="text-indigo-400 w-5 h-5" />
                <span className="text-white font-medium">{tr.bindHowTo}</span>
              </div>
              <ol className="text-sm text-zinc-300 space-y-2 ml-4 list-decimal">
                <li>{tr.bindStep1}</li>
                <li>{tr.bindStep2} <b className="text-white select-all">{serverIp}</b></li>
                <li>{tr.bindStep3}</li>
                <li>{tr.bindStep4}</li>
              </ol>
            </div>

            <div className="flex gap-3">
              <button onClick={onCancel} className="flex-1 py-3 rounded-xl bg-[#27272a] hover:bg-[#3f3f46] text-white font-medium transition">{tr.btnCancel}</button>
              <button onClick={handleRefresh} disabled={isRefreshingState} className="flex-[2] py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition flex items-center justify-center gap-2">
                <RefreshCw size={18} className={isRefreshingState ? 'animate-spin' : ''} />
                {isRefreshingState ? tr.btnChecking : tr.btnRefresh}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-white mb-2">{tr.finishTitle}</h2>
            <p className="text-zinc-400 text-sm mb-4">{tr.finishDesc}</p>

            <div className="space-y-3 mb-6">
              {hasJava && (
                <div onClick={() => setSelectedPlatform('java')} className={`p-4 rounded-xl border cursor-pointer transition-all ${selectedPlatform === 'java' ? 'bg-indigo-500/10 border-indigo-500' : 'bg-[#1e1e1e] border-white/5 hover:border-white/20'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">{tr.javaTitle}</span>
                      <div className="text-lg font-mono text-white mt-0.5">{user.mcNickname}</div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlatform === 'java' ? 'border-indigo-500' : 'border-zinc-600'}`}>
                      {selectedPlatform === 'java' && <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full" />}
                    </div>
                  </div>
                </div>
              )}
              {hasBedrock && (
                <div onClick={() => setSelectedPlatform('bedrock')} className={`p-4 rounded-xl border cursor-pointer transition-all ${selectedPlatform === 'bedrock' ? 'bg-indigo-500/10 border-indigo-500' : 'bg-[#1e1e1e] border-white/5 hover:border-white/20'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">{tr.bedrockTitle}</span>
                      <div className="text-lg font-mono text-white mt-0.5">{user.mcBedrock}</div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlatform === 'bedrock' ? 'border-indigo-500' : 'border-zinc-600'}`}>
                      {selectedPlatform === 'bedrock' && <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full" />}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <label className="flex items-center gap-3 mb-6 cursor-pointer group">
              <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${agreed ? 'bg-indigo-500 border-indigo-500' : 'border-zinc-600 group-hover:border-zinc-400'}`}>
                {agreed && <svg viewBox="0 0 14 14" fill="none" className="w-3 h-3 text-white"><path d="M3 7.5L5.5 10L11 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <input type="checkbox" className="hidden" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
              <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">{tr.agreeTerms}</span>
            </label>
            <div className="flex gap-3">
              <button onClick={onCancel} className="flex-1 py-3 rounded-xl bg-[#27272a] hover:bg-[#3f3f46] text-white font-medium transition">{tr.btnCancel}</button>
              <button onClick={() => onConfirm(selectedPlatform === 'java' ? user.mcNickname : user.mcBedrock)} disabled={!agreed || isProcessing} className={`flex-[2] py-3 rounded-xl text-white font-medium transition ${agreed ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'}`}>
                {isProcessing ? tr.btnCreating : tr.btnCreate}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

let globalRefreshLock: Promise<boolean> | null = null;

export const AuthProvider = ({ 
  children, 
  initialUser = null, 
  initialStatus = 'LOADING',
  initialRegionData = null
}: AuthProviderProps) => {
  const { t, locale } = useLanguage();
  const tb = t.auth.ban;

  // 1. Инициализируем стейт из серверных пропсов
  const [user, setUser] = useState<UserType | null>(initialUser);
  const [status, setStatus] = useState<AuthStatus>(initialStatus);
  const [regionData, setRegionData] = useState<RegionData | null>(initialRegionData);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');

  const [isIframeLoading, setIsIframeLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const pkceVerifierRef = useRef<string>('');
  const initLock = useRef(false);
  const pathname = usePathname();
  const router = useRouter();

  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);

  // 2. Если middleware не кэшировал регион, загружаем его
  useEffect(() => {
    if (!initialRegionData) {
      fetch(`${LOCAL_API_URL}/region`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => setRegionData(data))
        .catch(() => {});
    }
  }, [initialRegionData]);

  useEffect(() => {
    if (showLoginModal) { document.body.style.overflow = 'hidden'; } else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [showLoginModal]);

  const checkLocalAuth = useCallback(async (silent = false): Promise<boolean> => {
    try {
      const res = await fetch(`${LOCAL_API_URL}/auth/me`, { cache: 'no-store', credentials: 'include' });
      if (!res.ok) throw new Error('Bad response');
      const data = await res.json();
      if (data.status === 'REGISTERED' || data.status === 'NEEDS_REGISTRATION') {
        setUser(data.user);
        setStatus(data.status);
        return true;
      } else {
        if (!silent) { setUser(null); setStatus('GUEST'); }
        return false;
      }
    } catch {
      if (!silent) { setUser(null); setStatus('GUEST'); }
      return false;
    }
  }, []);

  const checkGlobalSSO = useCallback(async (): Promise<boolean> => {
    try {
      const fetchSSO = async () => {
        const res = await fetch(`${IDP_API_URL}/auth/check-cross-domain?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}`, { credentials: 'include', cache: 'no-store' });
        if (!res.ok) throw new Error('Bad SSO response');
        return await res.json();
      };
      let data = await fetchSSO();
      if (!data.authenticated) data = await fetchSSO();
      if (data.authenticated && data.auth_code && data.code_verifier) {
        try {
          const loginRes = await fetch(`${LOCAL_API_URL}/auth/login`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: data.auth_code, code_verifier: data.code_verifier }), credentials: 'include'
          });
          if (loginRes.ok) return await checkLocalAuth();
        } catch (e) { console.warn('Login request failed', e); }
      }
      return await checkLocalAuth(true);
    } catch { return false; }
  }, [checkLocalAuth]);

  // 3. Обновленная логика инициализации (учитывает SSR)
  useEffect(() => {
    if (initLock.current) return;
    initLock.current = true;
    const init = async () => {
      // Если сервер УЖЕ подтвердил авторизацию, пропускаем локальный чек
      if (initialStatus === 'REGISTERED' || initialStatus === 'NEEDS_REGISTRATION') {
        return; 
      }

      // Если сервер сказал GUEST, на всякий случай проверяем SSO (другой домен)
      try {
        const ssoRefreshed = await checkGlobalSSO();
        if (ssoRefreshed) { 
          router.refresh(); 
          return; 
        }
        setStatus('GUEST'); 
        setUser(null);
      } catch (error) { 
        setStatus('GUEST'); 
        setUser(null); 
      }
    };
    init();
  }, [checkLocalAuth, checkGlobalSSO, router, initialStatus]);

  useEffect(() => {
    const silentTokenCheck = async () => {
      if (statusRef.current !== 'REGISTERED' && statusRef.current !== 'NEEDS_REGISTRATION') return;
      const stillValid = await checkLocalAuth(true);
      if (stillValid) return;
      const recovered = await checkGlobalSSO();
      if (!recovered) { setUser(null); setStatus('GUEST'); }
    };
    const interval = setInterval(silentTokenCheck, SILENT_CHECK_INTERVAL_MS);
    const handleVisibility = () => { if (document.visibilityState === 'visible') silentTokenCheck(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', handleVisibility); };
  }, [checkLocalAuth, checkGlobalSSO]);

  const openLoginModal = async () => {
    setIsIframeLoading(true); setShowLoginModal(true);
    const verifier = generateRandomString(64);
    pkceVerifierRef.current = verifier;
    const challenge = await generateCodeChallenge(verifier);
    const url = `https://account.royolands.com/login-platform/?popup=true&transparent=1&client_id=${CLIENT_ID}&theme=dark&redirect_uri=${REDIRECT_URI}&code_challenge=${challenge}&code_challenge_method=S256#login`;
    setIframeUrl(url);
  };

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;
      const { type, auth_code, code, code_verifier: ssoVerifier } = event.data;
      const verifierToSend = ssoVerifier || pkceVerifierRef.current;
      const codeToSend = auth_code || code;

      if (type === 'close_popup') { setShowLoginModal(false); setIframeUrl(''); setIsIframeLoading(false); return; }
      if (type === 'login_success' || type === 'social_auth_success' || (auth_code && type !== 'webpackOk')) {
        setIsIframeLoading(false);
        if (!codeToSend || !verifierToSend) {
          if (type === 'login_success' || type === 'social_auth_success') {
            await checkLocalAuth(); router.refresh(); setShowLoginModal(false); return;
          }
          setShowLoginModal(false); setIframeUrl(''); return;
        }
        try {
          const res = await fetch(`${LOCAL_API_URL}/auth/login`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: codeToSend, code_verifier: verifierToSend }), credentials: 'include'
          });
          if (res.ok) { await checkLocalAuth(); router.refresh(); }
        } finally { setShowLoginModal(false); setIframeUrl(''); }
      }
    };
    if (showLoginModal) window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [showLoginModal, checkLocalAuth, router]);

  const register = async (selectedNickname?: string) => {
    if (!user) return;
    setIsRegistering(true);
    try {
      const res = await fetch(`${LOCAL_API_URL}/user`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oauthId: user.oauthId, nickname: selectedNickname || user.nickname, email: user.email }), credentials: 'include'
      });
      if (res.ok) await checkLocalAuth();
    } finally { setIsRegistering(false); }
  };

  const logout = async () => {
    try { await fetch(`${LOCAL_API_URL}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch {}
    setUser(null); setStatus('GUEST'); router.refresh();
  };

  const handleRefreshUser = useCallback(async (): Promise<boolean> => {
    if (globalRefreshLock) return await globalRefreshLock;
    globalRefreshLock = (async () => {
      try {
        let isAuth = await checkLocalAuth(true);
        if (isAuth) return true;
        const ssoRefreshed = await checkGlobalSSO();
        if (ssoRefreshed) { router.refresh(); return true; }
        return false;
      } catch (err) { return false; } finally { globalRefreshLock = null; }
    })();
    return await globalRefreshLock;
  }, [checkLocalAuth, checkGlobalSSO, router]);

  const isAuthenticated = status === 'REGISTERED';
  const isLoading = status === 'LOADING';

  const now = new Date();
  const hasStaffRights = user?.role === 'ADMIN' || user?.role === 'MODERATOR' || user?.hasAccess === true;
  const isBanned = user && !hasStaffRights && (user.isBanned || (user.bannedUntil && new Date(user.bannedUntil) > now));
  const isAccountStandingPage = pathname === '/account-standing' || pathname === '/shop' || pathname === '/support';

  return (
    <AuthContext.Provider value={{
      status, user, regionData, isAuthenticated, isLoading, logout, register,
      refreshUser: handleRefreshUser, openLoginModal, closeLoginModal: () => setShowLoginModal(false)
    }}>
      <style>{LOAD_STYLES}</style>

      {isBanned && !isAccountStandingPage ? (
        <div className="fixed inset-0 z-[9999999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/landing2.0/kizrum.png')" }} />
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-[#18181b] border border-white/10 rounded-[24px] p-8 max-w-[420px] w-full text-center shadow-2xl">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <ShieldAlert className="text-red-500 w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{tb.title}</h2>
            <p className="text-zinc-400 mb-6 text-sm">{tb.desc}</p>
            <div className="bg-[#121212] border border-white/5 rounded-xl p-4 text-left mb-6">
              <div className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-1">{tb.reasonTitle}</div>
              <div className="text-sm text-zinc-200 leading-relaxed mb-3">{user?.banReason || tb.noReason}</div>
              {user?.bannedUntil && (
                <>
                  <div className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-1">{tb.untilTitle}</div>
                  <div className="text-sm text-zinc-200">{new Date(user.bannedUntil).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US')}</div>
                </>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <Link href="/account-standing" className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-zinc-200 transition flex items-center justify-center gap-2"><Info size={18} />{tb.btnAccount}</Link>
              <Link href="/support" className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-zinc-200 transition flex items-center justify-center gap-2"><Info size={18} />{tb.btnSupport}</Link>
              <Link href="/shop" className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-zinc-200 transition flex items-center justify-center gap-2"><Info size={18} />{tb.btnShop}</Link>
              <button onClick={logout} className="w-full py-3 bg-transparent text-zinc-500 hover:text-white font-medium rounded-xl hover:bg-white/5 transition flex items-center justify-center gap-2"><LogOut size={18} />{tb.btnLogout}</button>
            </div>
          </div>
        </div>
      ) : (
        children
      )}

     {showLoginModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" />
          {isIframeLoading && (
            <div className="loading-cube-standalone relative z-[100000]">
              <div className="spinner"></div>
            </div>
          )}
      {iframeUrl && (
            <iframe
              src={iframeUrl}
              onLoad={() => setIsIframeLoading(false)}
              className="absolute inset-0 w-full h-full z-[100000] transition-opacity duration-300"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                colorScheme: 'normal',
                opacity: isIframeLoading ? 0 : 1
              }}
              allowTransparency={true}
              allow="autoplay; fullscreen"
            />
          )}
        </div>
      )}

      {status === 'NEEDS_REGISTRATION' && user && !isBanned && (
        <RegistrationModal 
          user={user} 
          regionData={regionData}
          onConfirm={register} 
          onCancel={logout} 
          isProcessing={isRegistering} 
          refreshUser={handleRefreshUser} 
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);